// Optimized Pusher Session Tracker with rrweb
// Key optimizations:
// 1. Event filtering - no mouse movements, minimal scroll events
// 2. Batch sending - events sent every 500ms via HTTP
// 3. Efficient recording - sampling and throttling

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import Pusher from 'pusher-js';
import { secureStorage } from '../utils/secureStorage';

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || 'cfc00fbbcf3d28bd5df1';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'ap2';
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const SESSION_KEY = 'godmode_session_id';
const CART_UPDATE_EVENT = 'godmode-cart-update';
const EVENT_BATCH_INTERVAL = 3000; // Send events every 3s — reduced from 200ms to prevent browser freeze

const PusherSessionTracker = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { cart, getTotalPrice } = useCart();
  
  // Refs
  const pusherRef = useRef(null);
  const sessionChannelRef = useRef(null);
  const heartbeatRef = useRef(null);
  const sessionIdRef = useRef(null);
  const geoLocationRef = useRef(null);
  const isReadyRef = useRef(false);
  
  // rrweb refs
  const rrwebRef = useRef(null);
  const stopRecordingRef = useRef(null);
  const isBeingWatchedRef = useRef(false);
  const watchingAdminRef = useRef(null);
  const eventQueueRef = useRef([]);
  const batchIntervalRef = useRef(null);
  const hasFullSnapshotRef = useRef(false);
  
  // Data refs
  const userRef = useRef(null);
  const isAuthenticatedRef = useRef(false);
  const cartRef = useRef([]);
  const getTotalPriceRef = useRef(null);
  
  // Keep refs in sync
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { isAuthenticatedRef.current = isAuthenticated; }, [isAuthenticated]);
  useEffect(() => { cartRef.current = cart; }, [cart]);
  useEffect(() => { getTotalPriceRef.current = getTotalPrice; }, [getTotalPrice]);
  
  // Get session ID
  const getSessionId = useCallback(() => {
    if (sessionIdRef.current) return sessionIdRef.current;
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    sessionIdRef.current = id;
    return id;
  }, []);
  
  // Get geolocation
  const getGeoLocation = useCallback(() => {
    if (geoLocationRef.current) return geoLocationRef.current;
    const cached = localStorage.getItem('user_geo');
    if (cached) {
      try { geoLocationRef.current = JSON.parse(cached); return geoLocationRef.current; } catch (e) {}
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const geo = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          geoLocationRef.current = geo;
          localStorage.setItem('user_geo', JSON.stringify(geo));
        },
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }
    return geoLocationRef.current;
  }, []);
  
  const getDeviceType = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|ipod/i.test(ua)) {
      return window.innerWidth < 768 ? 'mobile' : 'tablet';
    }
    return 'desktop';
  }, []);
  
  const getCartData = useCallback(() => {
    const currentCart = cartRef.current || [];
    const getTotal = getTotalPriceRef.current;
    return {
      cart_items: currentCart.map(item => ({
        jp_part_no: item.jp_part_no,
        qty: item.quantity,
        name: item.name || item.description,
        price: item.discounted_price || item.mrp
      })),
      cart_total: getTotal?.() || 0
    };
  }, []);

  // Send batched events to server
  const sendEventBatch = useCallback(async () => {
    if (!isBeingWatchedRef.current || !watchingAdminRef.current) return;
    if (eventQueueRef.current.length === 0) return;
    
    const events = [...eventQueueRef.current];
    eventQueueRef.current = [];
    
    console.log('[Tracker] 📤 Sending', events.length, 'events');
    
    try {
      await fetch(`${API_URL}/api/pusher/rrweb-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          adminChannel: watchingAdminRef.current,
          events: events,
          url: location.pathname,
          hasFullSnapshot: hasFullSnapshotRef.current
        })
      });
    } catch (e) {
      console.error('[Tracker] Batch send error:', e);
      // Put events back for retry
      eventQueueRef.current = [...events, ...eventQueueRef.current];
    }
  }, [location.pathname]);

  // Start rrweb recording
  const startRecording = useCallback(async (adminChannel) => {
    console.log('[Tracker] 🎬 startRecording called, adminChannel:', adminChannel);
    console.log('[Tracker] stopRecordingRef.current exists?', !!stopRecordingRef.current);
    
    // If already recording for same admin, skip
    if (stopRecordingRef.current && watchingAdminRef.current === adminChannel) {
      console.log('[Tracker] Already recording for this admin, skipping');
      return;
    }
    
    // If recording for different admin, stop first
    if (stopRecordingRef.current) {
      console.log('[Tracker] Stopping previous recording...');
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
    
    console.log('[Tracker] 🎬 Starting rrweb recording for:', adminChannel);
    isBeingWatchedRef.current = true;
    watchingAdminRef.current = adminChannel;
    hasFullSnapshotRef.current = false;
    eventQueueRef.current = [];
    
    try {
      // Load rrweb
      if (!rrwebRef.current) {
        console.log('[Tracker] Loading rrweb...');
        const rrweb = await import('rrweb');
        rrwebRef.current = rrweb;
        console.log('[Tracker] ✅ rrweb loaded');
      }
      
      const { record } = rrwebRef.current;
      
      // Start recording - rrweb will emit a FullSnapshot immediately
      console.log('[Tracker] 🎥 Calling rrweb.record()...');
      stopRecordingRef.current = record({
        emit: (event) => {
          // Track if we have a full snapshot
          if (event.type === 2) { // FullSnapshot
            hasFullSnapshotRef.current = true;
            console.log('[Tracker] 📸 Full snapshot captured! Event queue size:', eventQueueRef.current.length);
          }
          
          // Add to queue
          eventQueueRef.current.push(event);
        },
        // OPTIMIZATIONS:
        sampling: {
          // Don't record mouse movements at all
          mousemove: false,
          mouseInteraction: true, // Record clicks
          scroll: 150, // Sample scroll every 150ms
          media: 800, // Media sampling
          input: 'last' // Only last input value
        },
        // Block problematic elements
        blockClass: 'rr-block',
        ignoreClass: 'rr-ignore',
        // Reduce mutations
        slimDOMOptions: {
          script: true,
          comment: true,
          headFavicon: true,
          headWhitespace: true,
          headMetaSocial: true,
          headMetaRobots: true,
          headMetaHttpEquiv: true,
          headMetaVerification: true,
          headMetaAuthorship: true,
        },
        // Inline styles for accuracy
        inlineStylesheet: true,
        // Mask input values for security
        maskAllInputs: false,
        maskTextSelector: '.sensitive-data',
        // CRITICAL: Ensure we get a full snapshot
        checkoutEveryNms: 10000 // Take full snapshot every 10 seconds as backup
      });
      
      console.log('[Tracker] ✅ Recording started, stopFn exists:', !!stopRecordingRef.current);
      
      // Force a full snapshot using rrweb's takeFullSnapshot
      // This is needed because if recording was already running, we won't get another automatic one
      if (rrwebRef.current.takeFullSnapshot) {
        console.log('[Tracker] 📸 Forcing full snapshot...');
        rrwebRef.current.takeFullSnapshot();
      }
      
      // Start batch sending interval
      if (batchIntervalRef.current) {
        clearInterval(batchIntervalRef.current);
      }
      batchIntervalRef.current = setInterval(sendEventBatch, EVENT_BATCH_INTERVAL);
      
      // Send first batch immediately after a short delay (to capture full snapshot)
      setTimeout(() => {
        console.log('[Tracker] 📤 Sending initial batch, queue size:', eventQueueRef.current.length, 'hasFullSnapshot:', hasFullSnapshotRef.current);
        sendEventBatch();
      }, 500); // Increased delay to ensure snapshot is captured
      
    } catch (e) {
      console.error('[Tracker] Failed to start recording:', e);
      isBeingWatchedRef.current = false;
      watchingAdminRef.current = null;
    }
  }, [sendEventBatch]);

  // Stop recording
  const stopRecording = useCallback(() => {
    console.log('[Tracker] 🛑 Stopping recording');
    
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
    
    if (batchIntervalRef.current) {
      clearInterval(batchIntervalRef.current);
      batchIntervalRef.current = null;
    }
    
    // Send any remaining events
    if (eventQueueRef.current.length > 0) {
      sendEventBatch();
    }
    
    isBeingWatchedRef.current = false;
    watchingAdminRef.current = null;
    hasFullSnapshotRef.current = false;
    eventQueueRef.current = [];
  }, [sendEventBatch]);
  
  // Main connection
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    
    if (pusherRef.current) {
      const state = pusherRef.current.connection?.state;
      if (state === 'connected' || state === 'connecting') return;
    }
    
    const sessionId = getSessionId();
    console.log('[Tracker] 🚀 Init session:', sessionId);
    
    const pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
      authEndpoint: `${API_URL}/api/pusher/auth`
    });
    
    pusherRef.current = pusher;
    
    const channelName = `private-session-${sessionId}`;
    const sessionChannel = pusher.subscribe(channelName);
    sessionChannelRef.current = sessionChannel;
    
    sessionChannel.bind('pusher:subscription_succeeded', () => {
      console.log('[Tracker] ✅ Channel ready:', channelName);
      isReadyRef.current = true;
      
      // Join session
      const geo = getGeoLocation();
      const userName = userRef.current?.name || userRef.current?.company_name || 'Guest';
      const currentCartData = getCartData();
      
      fetch(`${API_URL}/api/pusher/session/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          user_id: userRef.current?.customer_id,
          user_name: userName,
          company_name: userRef.current?.company_name,
          is_guest: !isAuthenticatedRef.current,
          current_path: location.pathname,
          device_type: getDeviceType(),
          location: geo,
          ...currentCartData
        })
      }).catch(() => {});
      
      // Send another update after 1 second to capture any late-loading auth state
      setTimeout(() => {
        const updatedUserName = userRef.current?.name || userRef.current?.company_name || 'Guest';
        if (updatedUserName !== userName) {
          fetch(`${API_URL}/api/pusher/session/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              user_id: userRef.current?.customer_id,
              user_name: updatedUserName,
              company_name: userRef.current?.company_name,
              is_guest: !isAuthenticatedRef.current,
              current_path: location.pathname,
              device_type: getDeviceType()
            })
          }).catch(() => {});
        }
      }, 1000);
      
      // ALSO sync cart to Auth heartbeat immediately (for Live Market Pulse)
      const token = secureStorage.getToken();
      if (token) {
        const currentCart = cartRef.current || [];
        const formattedItems = currentCart.map(item => ({
          jp_part_no: item.jp_part_no,
          part_name: item.name || item.description || '',
          quantity: item.quantity,
          mrp: item.discounted_price || item.mrp
        }));
        
        fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            cart_items: formattedItems,
            cart_total: currentCartData.cart_total || 0,
            cart_items_count: currentCart.reduce((t, i) => t + (i.quantity || 1), 0),
            current_page: location.pathname,
            latitude: geo?.lat,
            longitude: geo?.lng
          })
        }).catch(() => {});
      }
      
      // Listen for watch commands
      sessionChannel.bind('start-watching', (data) => {
        console.log('[Tracker] 👁️ Admin started watching:', data);
        if (data?.adminChannel) {
          startRecording(data.adminChannel);
        }
      });
      
      sessionChannel.bind('stop-watching', () => {
        console.log('[Tracker] 👁️ Admin stopped watching');
        stopRecording();
      });
      
      sessionChannel.bind('request-snapshot', (data) => {
        console.log('[Tracker] 📸 Snapshot requested:', data);
        if (data?.adminChannel) {
          startRecording(data.adminChannel);
        }
      });
    });
    
    // Cart updates - Send to BOTH Pusher and Auth heartbeat endpoints
    const handleCartUpdate = (e) => {
      const cartItems = e.detail?.cart || [];
      const cartTotal = cartItems.reduce((t, i) => t + ((i.discounted_price || i.mrp) * i.quantity), 0);
      const cartItemsCount = cartItems.reduce((t, i) => t + i.quantity, 0);
      
      // Format cart items for API
      const formattedItems = cartItems.map(item => ({
        jp_part_no: item.jp_part_no,
        part_name: item.name || item.description || '',
        quantity: item.quantity,
        mrp: item.discounted_price || item.mrp
      }));
      
      // Send to Pusher heartbeat (for God Mode)
      fetch(`${API_URL}/api/pusher/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          cart_items: formattedItems,
          cart_total: cartTotal,
          current_path: location.pathname
        })
      }).catch(() => {});
      
      // ALSO send to Auth heartbeat (for Live Market Pulse) - with auth token
      const token = secureStorage.getToken();
      if (token) {
        fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            cart_items: formattedItems,
            cart_total: cartTotal,
            cart_items_count: cartItemsCount,
            current_page: location.pathname,
            latitude: geoLocationRef.current?.lat,
            longitude: geoLocationRef.current?.lng
          })
        }).catch(() => {});
      }
    };
    window.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    
    // Heartbeat - Send to BOTH Pusher and Auth endpoints
    heartbeatRef.current = setInterval(() => {
      if (!sessionIdRef.current) return;
      
      const cartData = getCartData();
      const currentCart = cartRef.current || [];
      const formattedItems = currentCart.map(item => ({
        jp_part_no: item.jp_part_no,
        part_name: item.name || item.description || '',
        quantity: item.quantity,
        mrp: item.discounted_price || item.mrp
      }));
      
      // Send to Pusher heartbeat (for God Mode)
      fetch(`${API_URL}/api/pusher/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          current_path: location.pathname,
          device_type: getDeviceType(),
          location: geoLocationRef.current,
          ...cartData
        })
      }).catch(() => {});
      
      // ALSO send to Auth heartbeat (for Live Market Pulse)
      const token = secureStorage.getToken();
      if (token) {
        fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            cart_items: formattedItems,
            cart_total: cartData.cart_total || 0,
            cart_items_count: currentCart.reduce((t, i) => t + (i.quantity || 1), 0),
            current_page: location.pathname,
            latitude: geoLocationRef.current?.lat,
            longitude: geoLocationRef.current?.lng
          })
        }).catch(() => {});
      }
    }, 30000); // Every 30 seconds — reduced from 5s to prevent network storm
    
    // Cleanup
    return () => {
      if (!pusherRef.current) return;
      const state = pusherRef.current.connection?.state;
      if (state === 'connecting') return;
      
      window.removeEventListener(CART_UPDATE_EVENT, handleCartUpdate);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      
      stopRecording();
      
      isReadyRef.current = false;
      
      if (state === 'connected') {
        fetch(`${API_URL}/api/pusher/session/leave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sessionIdRef.current })
        }).catch(() => {});
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, []);
  
  // Track last URL for page change detection
  const lastUrlRef = useRef(null);
  
  // On page navigation - take fresh snapshot if being watched
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    if (!isReadyRef.current || !sessionIdRef.current) return;
    
    const currentUrl = location.pathname;
    const previousUrl = lastUrlRef.current;
    
    // Update last URL
    lastUrlRef.current = currentUrl;
    
    // Update path heartbeat
    fetch(`${API_URL}/api/pusher/session/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionIdRef.current,
        current_path: currentUrl,
        device_type: getDeviceType(),
        ...getCartData()
      })
    }).catch(() => {});
    
    // CRITICAL: If being watched and page changed, take fresh full snapshot
    if (isBeingWatchedRef.current && previousUrl && previousUrl !== currentUrl) {
      console.log('[Tracker] 🔄 Page changed while being watched:', previousUrl, '->', currentUrl);
      
      // Stop current recording
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
        stopRecordingRef.current = null;
      }
      
      // Clear event queue and reset flags
      eventQueueRef.current = [];
      hasFullSnapshotRef.current = false;
      
      // Short delay then restart recording to capture new page
      setTimeout(async () => {
        if (isBeingWatchedRef.current && watchingAdminRef.current) {
          console.log('[Tracker] 🎬 Restarting recording for new page');
          
          const { record } = rrwebRef.current;
          
          // Start fresh recording
          stopRecordingRef.current = record({
            emit: (event) => {
              if (event.type === 2) {
                hasFullSnapshotRef.current = true;
                console.log('[Tracker] 📸 New page full snapshot captured');
              }
              eventQueueRef.current.push(event);
            },
            sampling: {
              mousemove: false,
              mouseInteraction: true,
              scroll: 150,
              media: 800,
              input: 'last'
            },
            blockClass: 'rr-block',
            ignoreClass: 'rr-ignore',
            slimDOMOptions: {
              script: true,
              comment: true,
              headFavicon: true,
              headWhitespace: true,
              headMetaSocial: true,
              headMetaRobots: true,
              headMetaHttpEquiv: true,
              headMetaVerification: true,
              headMetaAuthorship: true,
            },
            inlineStylesheet: true,
            maskAllInputs: false,
            maskTextSelector: '.sensitive-data'
          });
          
          // Send initial batch after short delay
          setTimeout(sendEventBatch, 300);
        }
      }, 100);
    }
    
  }, [location.pathname, getDeviceType, getCartData, sendEventBatch]);
  
  // Update user info on auth change
  useEffect(() => {
    if (!sessionIdRef.current || !isReadyRef.current) return;
    if (location.pathname.startsWith('/admin')) return;
    
    const userName = user?.name || user?.company_name || 'Guest';
    fetch(`${API_URL}/api/pusher/session/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: sessionIdRef.current,
        user_id: user?.customer_id,
        user_name: userName,
        company_name: user?.company_name,
        is_guest: !isAuthenticated,
        current_path: location.pathname,
        device_type: getDeviceType(),
        ...getCartData()
      })
    }).catch(() => {});
  }, [user, isAuthenticated, location.pathname, getDeviceType, getCartData]);
  
  // Market Pulse Heartbeat - Every 60 seconds, send PRECISE GPS and cart data to backend
  // This updates login_sessions for the Live Market Pulse dashboard
  const marketPulseHeartbeatRef = useRef(null);
  
  // Function to get fresh precise GPS coordinates
  const getPreciseLocation = () => {
    return new Promise((resolve) => {
      // First check localStorage for coordinates from LocationGate
      const storedCoords = localStorage.getItem('userCoordinates');
      if (storedCoords) {
        try {
          const coords = JSON.parse(storedCoords);
          if (coords.latitude && coords.longitude) {
            resolve(coords);
            return;
          }
        } catch (e) {}
      }
      
      // Try to get fresh coordinates from browser
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            };
            // Update localStorage
            localStorage.setItem('userCoordinates', JSON.stringify(coords));
            resolve(coords);
          },
          (error) => {
            console.log('[MarketPulse] Geolocation error:', error.message);
            // Fall back to any cached location
            const fallback = geoLocationRef.current || JSON.parse(localStorage.getItem('user_geo') || '{}');
            resolve({
              latitude: fallback?.lat || fallback?.latitude,
              longitude: fallback?.lng || fallback?.longitude
            });
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 60000 // Allow 1 minute cache
          }
        );
      } else {
        resolve({});
      }
    });
  };
  
  useEffect(() => {
    // Only run for authenticated users
    if (!isAuthenticated || !user) {
      if (marketPulseHeartbeatRef.current) {
        clearInterval(marketPulseHeartbeatRef.current);
        marketPulseHeartbeatRef.current = null;
      }
      return;
    }
    
    // Skip for admin pages
    if (location.pathname.startsWith('/admin')) return;
    
    const token = secureStorage.getToken();
    if (!token) return;
    
    // Function to send market pulse heartbeat with PRECISE GPS
    const sendMarketPulseHeartbeat = async () => {
      try {
        // Get precise GPS coordinates
        const geo = await getPreciseLocation();
        const cartData = getCartData();
        
        if (!geo.latitude || !geo.longitude) {
          console.log('[MarketPulse] No GPS coordinates available');
          return;
        }
        
        await fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            latitude: geo.latitude,
            longitude: geo.longitude,
            cart_total: cartData.cart_total || 0,
            cart_items_count: cartData.cart_items?.length || 0,
            cart_items: cartData.cart_items || [],  // Send full cart items for Live Pulse
            current_page: location.pathname
          })
        });
        console.log('[MarketPulse] 💓 Heartbeat sent with GPS:', geo.latitude.toFixed(4), geo.longitude.toFixed(4), 'Cart items:', cartData.cart_items?.length || 0);
      } catch (e) {
        console.log('[MarketPulse] Heartbeat failed:', e.message);
      }
    };
    
    // Send immediately on mount
    sendMarketPulseHeartbeat();
    
    // Then every 60 seconds
    marketPulseHeartbeatRef.current = setInterval(sendMarketPulseHeartbeat, 60000);
    
    return () => {
      if (marketPulseHeartbeatRef.current) {
        clearInterval(marketPulseHeartbeatRef.current);
        marketPulseHeartbeatRef.current = null;
      }
    };
  }, [isAuthenticated, user, location.pathname, getCartData]);
  
  return null;
};

export default PusherSessionTracker;
